/**
 * Element Renderer Component
 * Renders content elements and nested containers with type-specific previews
 * Uses Factory Pattern for clean, extensible element rendering
 */

import { Trash2 } from 'lucide-react';
import type { TemplateElement, ContainerElement } from '../../types/template.types';
import { ContainerRenderer } from './container-renderer';
import { elementRendererFactory } from './element-types/element-renderer-factory';
import type { ElementRenderContext } from './element-types/base-element-renderer';

interface ElementRendererProps {
  element: TemplateElement;
  globalFontFamily: string;
  globalFontSize: number;
  globalFontColor: string;
  isStretched?: { horizontal: boolean; vertical: boolean };
  maxElementHeight?: number;
  containerPath?: number[];
  selectedElementId?: string | null;
  onSelectElement?: (id: string | null) => void;
  onInsertClick?: (containerPath: number[], rowIndex: number, position: 'above' | 'below', cellRow?: number, cellColumn?: number) => void;
  onDelete?: (elementId: string) => void;
  cellInfo?: { containerId: string; row: number; col: number };
  rootContainerIds?: string[]; // IDs of root containers (header, footer, main containers)
}

export function ElementRenderer({ 
  element, 
  globalFontFamily,
  globalFontSize,
  globalFontColor,
  isStretched,
  maxElementHeight,
  containerPath,
  selectedElementId,
  onSelectElement,
  onInsertClick,
  onDelete,
  cellInfo,
  rootContainerIds = [],
}: ElementRendererProps) {
  // Nested container handling
  if (element.type === 'container') {
    const isSelected = selectedElementId === element.id;
    
    // Check if parent cell or row is selected
    const cellId = cellInfo ? `${cellInfo.containerId}-cell-${cellInfo.row}-${cellInfo.col}` : null;
    const rowId = cellInfo ? `${cellInfo.containerId}-row-${cellInfo.row}` : null;
    const isCellSelected = cellId ? selectedElementId === cellId : false;
    const isRowSelected = rowId ? selectedElementId === rowId : false;
    
    // Container uses its own alignment settings or isStretched from parent cell
    const containerElement = element as ContainerElement;
    const shouldStretchHorizontal = isStretched?.horizontal || containerElement.horizontalAlign === 'stretch';
    const shouldStretchVertical = isStretched?.vertical || containerElement.verticalAlign === 'stretch';
    
    // Calculate dimensions considering margins
    const marginTop = element.marginTop || 0;
    const marginBottom = element.marginBottom || 0;
    const marginLeft = element.marginLeft || 0;
    const marginRight = element.marginRight || 0;
    
    const containerWidth = shouldStretchHorizontal
      ? (marginLeft || marginRight) 
        ? `calc(100% - ${marginLeft}px - ${marginRight}px)` 
        : '100%'
      : (containerElement.width ? `${containerElement.width}px` : 'auto');
    const containerHeight = shouldStretchVertical
      ? (marginTop || marginBottom) 
        ? `calc(100% - ${marginTop}px - ${marginBottom}px)` 
        : '100%'
      : (containerElement.height ? `${containerElement.height}px` : 'auto');
    
    const handleClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      if (onSelectElement && cellInfo) {
        // Drill-Up: Container → Cell → Row → Parent Container
        if (isRowSelected) {
          // Row selected → Parent Container
          onSelectElement(cellInfo.containerId);
        } else if (isCellSelected) {
          // Cell selected → Row
          onSelectElement(rowId!);
        } else if (isSelected) {
          // Container selected → Cell
          onSelectElement(cellId!);
        } else {
          // Nothing selected → Container
          onSelectElement(element.id);
        }
      } else if (onSelectElement) {
        // No cellInfo - this is a root container, just toggle selection
        onSelectElement(isSelected ? '' : element.id);
      }
    };
    
    return (
      <div
        onClick={handleClick}
        className={isSelected ? 'ring-2 ring-blue-500 rounded' : ''}
        style={{
          position: 'relative',
          width: containerWidth,
          height: containerHeight,
          minWidth: isStretched?.horizontal ? 0 : undefined,
          minHeight: isStretched?.vertical ? 0 : undefined,
          // Apply margins - for stretch, margins are included in calc() above
          // For alignment modes, use auto for centering
          marginTop: shouldStretchVertical 
            ? (marginTop ? `${marginTop}px` : undefined)
            : (element.verticalAlign === 'middle' || element.verticalAlign === 'bottom') ? 'auto' 
            : (element.marginTop ? `${element.marginTop}px` : undefined),
          marginBottom: shouldStretchVertical 
            ? (marginBottom ? `${marginBottom}px` : undefined)
            : element.verticalAlign === 'middle' ? 'auto' 
            : (element.marginBottom ? `${element.marginBottom}px` : undefined),
          marginLeft: shouldStretchHorizontal 
            ? (marginLeft ? `${marginLeft}px` : undefined)
            : (element.horizontalAlign === 'center' || element.horizontalAlign === 'right') ? 'auto' 
            : (element.marginLeft ? `${element.marginLeft}px` : undefined),
          marginRight: shouldStretchHorizontal 
            ? (marginRight ? `${marginRight}px` : undefined)
            : element.horizontalAlign === 'center' ? 'auto' 
            : element.horizontalAlign === 'right' ? (element.marginRight ? `${element.marginRight}px` : 0) 
            : (element.marginRight ? `${element.marginRight}px` : undefined),
        }}
      >
        <ContainerRenderer
          container={element as ContainerElement}
          containerPath={containerPath || []}
          selectedElementId={selectedElementId || null}
          onSelectElement={onSelectElement || (() => {})}
          onInsertClick={onInsertClick || (() => {})}
          onDelete={onDelete || (() => {})}
          fontFamily={globalFontFamily}
          fontSize={globalFontSize}
          fontColor={globalFontColor}
          isDeletable={true}
          topMargin={undefined}
          bottomMargin={undefined}
          rootContainerIds={rootContainerIds}
        />
      </div>
    );
  }

  const style: React.CSSProperties = {
    fontFamily: element.fontFamily || globalFontFamily,
    fontSize: element.fontSize || globalFontSize,
    color: element.fontColor || globalFontColor,
    fontWeight: element.fontWeight,
    fontStyle: element.fontStyle,
    textDecoration: element.textDecoration,
    textAlign: element.textAlign,
    lineHeight: element.lineHeight,
    letterSpacing: element.letterSpacing ? `${element.letterSpacing}px` : undefined,
    backgroundColor: element.backgroundColor,
  };

  const isSelected = selectedElementId === element.id;
  
  // Check if parent cell or row is selected
  const cellId = cellInfo ? `${cellInfo.containerId}-cell-${cellInfo.row}-${cellInfo.col}` : null;
  const rowId = cellInfo ? `${cellInfo.containerId}-row-${cellInfo.row}` : null;
  const isCellSelected = cellId ? selectedElementId === cellId : false;
  const isRowSelected = rowId ? selectedElementId === rowId : false;
  
  // Check if element should stretch
  const shouldStretchHorizontal = element.horizontalAlign === 'stretch' || isStretched?.horizontal;
  const shouldStretchVertical = element.verticalAlign === 'stretch' || isStretched?.vertical;
  
  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onSelectElement && cellInfo) {
      console.log('ElementRenderer click:', {
        elementId: element.id,
        cellId,
        rowId,
        isSelected,
        isCellSelected,
        isRowSelected,
        selectedElementId
      });
      
      // Drill-Up: Element → Cell → Row → Container
      if (isRowSelected) {
        // Row selected → Container
        console.log('→ Selecting container:', cellInfo.containerId);
        onSelectElement(cellInfo.containerId);
      } else if (isCellSelected) {
        // Cell selected → Row
        console.log('→ Selecting row:', rowId);
        onSelectElement(rowId!);
      } else if (isSelected) {
        // Element selected → Cell
        console.log('→ Selecting cell:', cellId);
        onSelectElement(cellId!);
      } else {
        // Nothing selected → Element
        console.log('→ Selecting element:', element.id);
        onSelectElement(element.id);
      }
    } else if (onSelectElement) {
      // No cellInfo - just toggle element selection
      onSelectElement(isSelected ? '' : element.id);
    }
  };

  // Map vertical alignment to CSS
  const getVerticalAlignment = (): string | undefined => {
    if (shouldStretchVertical) return undefined;
    switch (element.verticalAlign) {
      case 'top': return 'flex-start';
      case 'middle': return 'center';
      case 'bottom': return 'flex-end';
      default: return undefined;
    }
  };

  const elementStyle: React.CSSProperties = {
    ...style,
    cursor: 'pointer',
    outline: isSelected ? '2px solid #3b82f6' : 'none',
    width: shouldStretchHorizontal ? '100%' : (element.width ? `${element.width}px` : undefined),
    height: shouldStretchVertical ? '100%' : (element.height ? `${element.height}px` : undefined),
    display: element.verticalAlign && !shouldStretchVertical ? 'flex' : undefined,
    flexDirection: element.verticalAlign && !shouldStretchVertical ? 'column' : undefined,
    justifyContent: getVerticalAlignment(),
    flexShrink: 0, // Prevent flex container from shrinking this element
    // Spacing
    marginTop: element.marginTop ? `${element.marginTop}px` : undefined,
    marginBottom: element.marginBottom ? `${element.marginBottom}px` : undefined,
    marginLeft: element.marginLeft ? `${element.marginLeft}px` : undefined,
    marginRight: element.marginRight ? `${element.marginRight}px` : undefined,
    paddingTop: element.paddingTop ? `${element.paddingTop}px` : undefined,
    paddingBottom: element.paddingBottom ? `${element.paddingBottom}px` : undefined,
    paddingLeft: element.paddingLeft ? `${element.paddingLeft}px` : undefined,
    paddingRight: element.paddingRight ? `${element.paddingRight}px` : undefined,
  };

  // Wrapper for delete button
  const ElementWrapper = ({ children }: { children: React.ReactNode }) => (
    <div style={{ 
      position: 'relative',
      width: shouldStretchHorizontal ? '100%' : undefined,
      height: shouldStretchVertical ? '100%' : undefined,
    }}>
      {children}
      {isSelected && onDelete && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDelete(element.id);
          }}
          className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 shadow-lg z-10"
          title="Element löschen"
        >
          <Trash2 className="w-3 h-3" />
        </button>
      )}
    </div>
  );

  // Create render context for factory
  const renderContext: ElementRenderContext = {
    element,
    globalFontFamily,
    globalFontSize,
    globalFontColor,
    isStretched,
    maxElementHeight,
    isSelected,
    baseStyle: style,
    elementStyle,
    shouldStretchHorizontal: shouldStretchHorizontal || false,
    shouldStretchVertical: shouldStretchVertical || false,
    onClick: handleClick,
  };

  // Use factory to get renderer for element type
  const renderer = elementRendererFactory.getRenderer(element.type);
  
  if (!renderer) {
    return <ElementWrapper><div>Unbekannter Elementtyp: {element.type}</div></ElementWrapper>;
  }

  // Render element using factory
  return <ElementWrapper>{renderer.render(renderContext)}</ElementWrapper>;
}
